import { forwardRef, useEffect, useState, type InputHTMLAttributes } from 'react';
import { currencyDisplayDigits, parseMoney } from '@his/shared';
import { display } from '@/lib/utils';
import { Input } from './ui/primitives';

export function moneyDigits(): 0 | 2 {
  return currencyDisplayDigits(display.currency);
}

export function parseMoneyInput(text: string): number | null {
  return parseMoney(text, display.locale, moneyDigits());
}

export function minorFromInput(text: string): number {
  if (!text.trim()) return 0;
  return parseMoneyInput(text) ?? Number.NaN;
}

export function formatMoneyInput(minor: number | null | undefined): string {
  if (minor === null || minor === undefined) return '';
  const digits = moneyDigits();
  return new Intl.NumberFormat(display.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false }).format(minor / 100);
}

type MoneyInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  valueMinor: number | null;
  onChangeMinor: (minor: number | null) => void;
};

export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput({ valueMinor, onChangeMinor, onBlur, ...rest }, ref) {
  const [text, setText] = useState(() => formatMoneyInput(valueMinor));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (parseMoneyInput(text) !== valueMinor) setText(formatMoneyInput(valueMinor));
  }, [valueMinor]);

  return (
    <Input
      {...rest}
      ref={ref}
      dir="ltr"
      inputMode={moneyDigits() === 0 ? 'numeric' : 'decimal'}
      className={`tabular ${rest.className ?? ''}`}
      aria-invalid={invalid || undefined}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        const parsed = event.target.value.trim() ? parseMoneyInput(event.target.value) : null;
        setInvalid(event.target.value.trim() !== '' && parsed === null);
        onChangeMinor(parsed);
      }}
      onBlur={(event) => {
        if (valueMinor !== null && !invalid) setText(formatMoneyInput(valueMinor));
        onBlur?.(event);
      }}
    />
  );
});
